import { useCallback } from "react";
import { type GlobalSelectionContextValue } from "@/hooks/useGlobalSelection";
import type { WidgetItem, HistoryItem, FolderDataLink } from "@/types";

interface UseAreaSelectionProps {
  globalSelection: GlobalSelectionContextValue;
  widgets: WidgetItem[];
  history: HistoryItem[];
  folderLinks: FolderDataLink[];
}

export function useAreaSelection({
  globalSelection,
  widgets,
  history,
  folderLinks,
}: UseAreaSelectionProps) {
  const handleSelectAll = useCallback(() => {
    const entries = globalSelection.entries;
    if (entries.length === 0) return;

    // Determine active area from last selected item
    const lastArea = entries[entries.length - 1].area;

    if (lastArea === 'widget') {
      const allEntries = widgets.map(w => ({
        area: 'widget' as const,
        itemId: w.id,
        dataId: w.dataId,
      }));
      globalSelection.clearAll();
      globalSelection.addEntries(allEntries);
    } else if (lastArea === 'history') {
      const allEntries = history.map(h => ({
        area: 'history' as const,
        itemId: h.id,
        dataId: h.dataId,
      }));
      globalSelection.clearAll();
      globalSelection.addEntries(allEntries);
    } else if (lastArea === 'folder') {
      // Find the folder that contains the selected item
      const selectedLinkId = entries.find(e => e.area === 'folder')?.itemId;
      const link = folderLinks.find(l => l.id === selectedLinkId);
      if (link) {
        const siblingLinks = folderLinks.filter(l => l.folderId === link.folderId);
        const allEntries = siblingLinks.map(l => ({
          area: 'folder' as const,
          itemId: l.id,
          dataId: l.dataId,
        }));
        globalSelection.clearAll();
        globalSelection.addEntries(allEntries);
      }
    }
  }, [globalSelection, widgets, history, folderLinks]);

  return { handleSelectAll };
}
